import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  DirectAnswer,
  Eyebrow,
  Lead,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta, ogImageUrl } from "@/lib/og";
import { articleSchema, jsonLdScript } from "@/lib/schema";
import { contactFunnel } from "@/lib/analytics";
import { RelatedLinks } from "@/components/site/RelatedLinks";

const PATH = "/what-a-technology-non-executive-director-does";
const CTA_SOURCE_BAND = "ned_guide_footer";
const REVIEWED = "6 August 2026";

const ANSWER =
  "A technology non-executive director is a board member, not an employee, who applies independent judgement to technology: whether the roadmap is real, what technology costs and where that is heading, where risk is concentrated in suppliers, security and key people, and whether the architecture survives growth. They hold the same statutory duties as any UK director under sections 171 to 177 of the Companies Act 2006.";

const title = "What Does a Technology NED Do? Duties and Board Role";
const description =
  "What a technology non-executive director does on a board: statutory duties, technical assurance, the questions they ask, and how it differs from a CTO.";

const DUTIES = [
  {
    title: "Act within powers",
    body: "Section 171: follow the company's constitution and exercise powers only for the purposes they were given for.",
  },
  {
    title: "Promote the success of the company",
    body: "Section 172: act in the way most likely to promote the success of the company for the benefit of its members as a whole, having regard to the listed factors.",
  },
  {
    title: "Exercise independent judgement",
    body: "Section 173: form your own view. For a NED this is the substance of the role, not a formality.",
  },
  {
    title: "Exercise reasonable care, skill and diligence",
    body: "Section 174: measured against both what is generally expected of a director in that role, and the knowledge and experience the individual actually has.",
  },
  {
    title: "Avoid conflicts of interest",
    body: "Section 175: this duty continues to apply after you cease to be a director in respect of property, information and opportunity.",
  },
  {
    title: "Not accept benefits from third parties",
    body: "Section 176: and this duty also survives leaving the board.",
  },
  {
    title: "Declare interests in transactions",
    body: "Section 177: declare the nature and extent of any interest in a proposed transaction or arrangement with the company.",
  },
];

const ASSURANCE = [
  {
    title: "Is the roadmap real?",
    body: "Boards are usually shown progress reported by the people doing the work. A technology NED reads the delivery record against what was promised and tells the board what is actually true.",
  },
  {
    title: "What does the technology cost, and where is that going?",
    body: "Run rate, licences, cloud, suppliers, technical debt being serviced rather than repaid. Cost surprises in technology are almost always visible a year before they land.",
  },
  {
    title: "Where is the concentrated risk?",
    body: "Key-person dependency, a single supplier holding the estate, security and data protection exposure, regulated obligations that nobody on the board can currently test.",
  },
  {
    title: "Can the business survive its own growth?",
    body: "Architecture and team structure that work at today's volume and fail at three times it. This is the question investors ask in diligence, and it is better answered before they ask.",
  },
];

const FAQ = [
  {
    q: "What does a non-executive director do?",
    a: "A non-executive director sits on the board but is not an employee and does not run the business day to day. The role is oversight and independent judgement: contributing to strategy, scrutinising the performance of the executive, satisfying themselves that financial information and controls are sound, and taking part in appointing and removing senior management.",
  },
  {
    q: "Does a non-executive director have the same legal duties as an executive director?",
    a: "Yes. The seven general duties in sections 171 to 177 of the Companies Act 2006 are owed by every director of a UK company. There is no reduced standard for non-executives. Section 174's care, skill and diligence test is partly subjective, so a director with deep technical experience is judged against that experience.",
  },
  {
    q: "What is a technology non-executive director specifically?",
    a: "A non-executive director whose independent judgement is applied to the technology side of the business: delivery, architecture, technology spend, security and data protection, supplier arrangements, and the technical questions that arise in fundraising and diligence. The governance role is the same; the domain of assurance is technology.",
  },
  {
    q: "How much time does a non-executive director role take?",
    a: "It varies by company size, sector and committee load, and it has been rising. Rather than quote a figure that would be out of date, the current benchmark surveys are listed in the sources section of this page.",
  },
  {
    q: "What is the difference between a non-executive director and a fractional CTO?",
    a: "Accountability. A NED holds a statutory office under the Companies Act 2006 and answers to the company as a whole, so the role is oversight of the executive. A fractional CTO holds no office, sits inside the executive line and answers to the founder or chief executive for the delivery of technical work. One assures, the other builds, and the same person should not do both in the same company at the same time.",
  },
  {
    q: "Does the UK Corporate Governance Code apply to private companies?",
    a: "Not directly. The FRC's UK Corporate Governance Code applies to companies in the commercial companies and closed-ended investment funds categories of the UK Listing Rules. Private company boards frequently use it as a reference point for good practice, but compliance is not a legal requirement for them. The Companies Act duties, by contrast, apply to every UK company director.",
  },
];

export const Route = createFileRoute("/what-a-technology-non-executive-director-does")({
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "what-a-technology-non-executive-director-does",
        "Guide: what a technology non-executive director actually does",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        articleSchema({
          path: PATH,
          headline: title,
          description,
          datePublished: "2026-08-03",
          dateModified: "2026-08-06",
          image: ogImageUrl("what-a-technology-non-executive-director-does"),
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
            {
              "@type": "ListItem",
              position: 2,
              name: "Board and advisory",
              item: `${SITE.url}/board-advisory`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "What a technology non-executive director does",
              item: `${SITE.url}${PATH}`,
            },
          ],
        }),
      },
    ],
  }),
  component: NedGuidePage,
});

function NedGuidePage() {
  return (
    <div>
      <section
        aria-labelledby="nedg-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Guide</Eyebrow>
          <h1
            id="nedg-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            What a technology non-executive director actually does
          </h1>
          <DirectAnswer className="mt-6">{ANSWER}</DirectAnswer>
          <Lead className="mt-6">
            The legal duties, the board responsibilities, the technical assurance a board needs, and
            how the role differs from a fractional CTO or a consultant. Sourced from the Companies
            Act 2006 and the FRC&rsquo;s UK Corporate Governance Code, not from received wisdom.
          </Lead>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="nedg-what-heading">
        <Container>
          <h2
            id="nedg-what-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            What does a technology non-executive director do?
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body md:text-[19px]">
            <p>
              A non-executive director is a member of the board who is not an employee and does not
              run the business day to day. The role is independent judgement: contributing to
              strategy, scrutinising how the executive is performing against it, satisfying
              themselves that financial information and controls are sound, and taking part in
              appointing, removing and paying senior management.
            </p>
            <p>
              A <em>technology</em> non-executive director applies that same independent judgement
              to the part of the business most boards are least equipped to test, delivery,
              architecture, technology spend, security, data protection and supplier risk.
            </p>
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="nedg-duties-heading">
        <Container>
          <Eyebrow>The legal position</Eyebrow>
          <h2
            id="nedg-duties-heading"
            className="mt-3 max-w-[30ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            What are a non-executive director&rsquo;s legal duties?
          </h2>
          <p className="mt-6 max-w-[62ch] text-base leading-relaxed text-body">
            Sections 171 to 177 of the Companies Act 2006 codify seven general duties owed by a
            director to the company. They apply to every UK company director, executive or
            non-executive, in a listed group or a small private company. There is no lighter
            standard for non-executives.
          </p>
          <ol className="mt-8 grid gap-5 sm:grid-cols-2">
            {DUTIES.map((item, i) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">
                  <span className="text-muted-foreground">{i + 1}. </span>
                  {item.title}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ol>
          <p className="mt-6 max-w-[70ch] text-[15px] leading-relaxed text-muted-foreground">
            Section 174 sets the care, skill and diligence standard by reference both to what is
            generally expected of a director carrying out that function and to the knowledge, skill
            and experience the individual actually has. A director appointed for technical expertise
            is judged against that expertise. Source: Companies Act 2006, sections 170 to 177.
          </p>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="nedg-assurance-heading">
        <Container>
          <Eyebrow>Technical assurance</Eyebrow>
          <h2
            id="nedg-assurance-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            The four questions a board cannot usually answer itself
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {ASSURANCE.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="nedg-code-heading">
        <Container>
          <h2
            id="nedg-code-heading"
            className="max-w-[28ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Where the governance codes fit
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              The FRC published the current UK Corporate Governance Code on 22 January 2024. It has
              applied to accounting periods beginning on or after 1 January 2025, with Provision 29
              , the board&rsquo;s declaration on the effectiveness of material internal controls,
              applying to financial years beginning on or after 1 January 2026.
            </p>
            <p>
              The Code applies to companies in the commercial companies and closed-ended investment
              funds categories of the UK Listing Rules. It is not law for private companies, and any
              adviser telling a private board that it must comply is wrong. Private boards do
              commonly use it as a reference for good practice, and the direction of travel on
              internal controls is worth understanding early if a listing or a trade sale is
              plausible.
            </p>
            <p>
              What is not optional for anyone is the Companies Act. Those duties bind the director
              of a two-person company exactly as they bind a FTSE 100 non-executive.
            </p>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="nedg-vs-heading">
        <Container>
          <h2
            id="nedg-vs-heading"
            className="max-w-[28ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            NED, fractional CTO or consultant
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              A <strong>non-executive director</strong> is on the board, carries the full statutory
              duties above, and provides independent oversight of the executive. A{" "}
              <strong>fractional CTO</strong> works inside the business, part-time, owning and
              shaping technical decisions. A <strong>consultant</strong> is engaged for a defined
              piece of work and has no standing in the governance of the company.
            </p>
            <p>
              These should not be collapsed into one person in the same business at the same time.
              Independent assurance stops being independent when the person giving it is assuring
              their own work.
            </p>
          </div>
          <RelatedLinks
            className="mt-7"
            paths={[
              "/technology-non-executive-director-uk",
              "/fractional-cto-vs-full-time-vs-agency",
              "/non-executive-director-vs-advisor-vs-chair",
            ]}
          />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="nedg-quote-heading">
        <Container>
          <h2 id="nedg-quote-heading" className="sr-only">
            What boards say
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Quote item={TESTIMONIALS.steveKelly} />
            <Quote item={TESTIMONIALS.richardGregory} />
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="nedg-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="nedg-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Non-executive director FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="nedg-sources-heading">
        <Container>
          <h2 id="nedg-sources-heading" className="text-xl font-bold tracking-[-0.02em]">
            Sources
          </h2>
          <ul className="mt-5 max-w-[80ch] space-y-4 text-[15px] leading-relaxed text-body">
            <li>
              Companies Act 2006, Part 10 Chapter 2, sections 170 to 177 (general duties of
              directors).{" "}
              <a
                className="font-semibold underline underline-offset-2 hover:text-primary"
                href="https://www.legislation.gov.uk/ukpga/2006/46/part/10/chapter/2"
                rel="noopener noreferrer"
                target="_blank"
              >
                legislation.gov.uk
              </a>
              . Accessed 3 August 2026.
            </li>
            <li>
              Financial Reporting Council, UK Corporate Governance Code 2024 (published 22 January
              2024; applies to accounting periods beginning on or after 1 January 2025, Provision 29
              to financial years beginning on or after 1 January 2026).{" "}
              <a
                className="font-semibold underline underline-offset-2 hover:text-primary"
                href="https://www.frc.org.uk/library/standards-codes-policy/corporate-governance/uk-corporate-governance-code/"
                rel="noopener noreferrer"
                target="_blank"
              >
                frc.org.uk
              </a>
              . Accessed 3 August 2026.
            </li>
            <li>
              For current NED fee and time-commitment benchmarks: MM&amp;K,{" "}
              <cite>Life in the Boardroom: 2025/2026 Chair and Non-Executive Director Survey</cite>{" "}
              (October 2025, 168 directors covering 435 board appointments), and Korn Ferry,{" "}
              <cite>2026 Non-Executive Director Pay Report</cite> (February 2026). Figures are not
              reproduced here because both are updated annually and a number quoted on a web page
              goes stale faster than the page does.
            </li>
          </ul>
          <p className="mt-6 max-w-[80ch] text-[15px] leading-relaxed text-muted-foreground">
            This guide is general information about the role, not legal advice on a specific
            appointment. Where it draws on my own board experience rather than a cited source, it
            says so.
          </p>
          <p className="mt-4 text-[15px] text-muted-foreground">
            Last reviewed: <time dateTime="2026-08-06">{REVIEWED}</time>
          </p>
        </Container>
      </section>

      <CtaBand
        heading="Considering a technology NED?"
        body={`Tell me about the board, the stage the business is at, and the technical question nobody can currently answer. Based in ${SITE.location}, working with boards in the UK and internationally.`}
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
